import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { adminUserService, User } from '@/services/adminUserService';
import { adminPaymentService } from '@/services/adminPaymentService';
import { toastHelper } from '@/utils/toastHelper';
import { RefreshCw, Search, User as UserIcon, Eye, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';

const UserManagement: React.FC = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const pageSize = 25;

  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPlanId, setSelectedPlanId] = useState<string>('');
  const [selectedTier, setSelectedTier] = useState<string>('');
  const [selectedBillingCycle, setSelectedBillingCycle] = useState<string>('');

  // Sorting states
  const [sortBy, setSortBy] = useState<string>('date_created');
  const [sortOrder, setSortOrder] = useState<string>('desc');

  // Available plans for filtering
  const [availablePlans, setAvailablePlans] = useState<Array<{ id: string; name: string; tier: string; billing_cycle: string }>>([]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const params: any = {
        limit: pageSize,
        offset: currentPage * pageSize,
        sort_by: sortBy,
        sort_order: sortOrder
      };
      if (searchQuery) params.search = searchQuery;
      if (selectedPlanId) params.plan_id = selectedPlanId;
      if (selectedTier) params.tier = selectedTier;
      if (selectedBillingCycle) params.billing_cycle = selectedBillingCycle;

      const data = await adminUserService.getUsers(params);
      setUsers(data.users);
      setTotalCount(data.total);
    } catch (error: any) {
      toastHelper.newMessage('error', 'Error', error.response?.data?.message || 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const fetchPlans = useCallback(async () => {
    try {
      const data = await adminPaymentService.listUnifiedPlans();
      const plansList: Array<{ id: string; name: string; tier: string; billing_cycle: string }> = [];

      data.plans.forEach((plan: any) => {
        if (plan.monthly_plans) {
          Object.values(plan.monthly_plans).forEach((p: any) => {
            plansList.push({
              id: p.paypal_plan_id,
              name: `${plan.name} - ${p.tier} Monthly`,
              tier: p.tier,
              billing_cycle: 'monthly'
            });
          });
        }
        if (plan.annual_plans) {
          Object.values(plan.annual_plans).forEach((p: any) => {
            plansList.push({
              id: p.paypal_plan_id,
              name: `${plan.name} - ${p.tier} Annual`,
              tier: p.tier,
              billing_cycle: 'annual'
            });
          });
        }
      });

      setAvailablePlans(plansList);
    } catch (error: any) {
      console.error('Failed to fetch plans for filtering:', error);
    }
  }, []);

  // Initial fetch on mount
  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(0);
  }, [searchQuery, selectedPlanId, selectedTier, selectedBillingCycle]);

  // Fetch users with debounce on search, immediate on other changes
  useEffect(() => {
    const isSearchChange = searchQuery !== '';
    const timer = setTimeout(
      () => {
        fetchUsers();
      },
      isSearchChange ? 300 : 0
    );
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, selectedPlanId, selectedTier, selectedBillingCycle, currentPage, sortBy, sortOrder]);

  const handleViewUser = (userId: string) => {
    navigate(`/admin/user-management/${userId}`);
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedPlanId('');
    setSelectedTier('');
    setSelectedBillingCycle('');
    setCurrentPage(0);
  };

  const handleSort = (column: string) => {
    if (sortBy === column) {
      // Toggle sort order if clicking the same column
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new column and default to descending for date_created, ascending for others
      setSortBy(column);
      setSortOrder(column === 'date_created' ? 'desc' : 'asc');
    }
    setCurrentPage(0); // Reset to first page when sorting changes
  };

  const uniqueTiers = useMemo(() => {
    const tiers = new Set<string>();
    users.forEach(user => {
      if (user.subscription_tier) tiers.add(user.subscription_tier);
    });
    return Array.from(tiers).sort();
  }, [users]);

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold">User Management</h2>
          <p className="text-sm sm:text-base text-muted-foreground">Manage users, subscriptions, and role assignments</p>
        </div>
        <Button onClick={fetchUsers} variant="outline" size="sm" className="self-start sm:self-auto">
          <RefreshCw className="h-4 w-4 sm:mr-2" />
          <span className="hidden sm:inline">Refresh</span>
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
          <CardDescription>Filter users by various criteria</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label htmlFor="search">Search Email</Label>
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  id="search"
                  placeholder="Search by email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-8"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="plan">Plan</Label>
              <Select value={selectedPlanId || "all"} onValueChange={(value) => setSelectedPlanId(value === "all" ? "" : value)}>
                <SelectTrigger id="plan">
                  <SelectValue placeholder="All plans" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All plans</SelectItem>
                  {availablePlans.map((plan) => (
                    <SelectItem key={plan.id} value={plan.id}>
                      {plan.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="tier">Tier</Label>
              <Select value={selectedTier || "all"} onValueChange={(value) => setSelectedTier(value === "all" ? "" : value)}>
                <SelectTrigger id="tier">
                  <SelectValue placeholder="All tiers" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All tiers</SelectItem>
                  {uniqueTiers.map((tier) => (
                    <SelectItem key={tier} value={tier}>
                      {tier.charAt(0).toUpperCase() + tier.slice(1)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="billing">Billing Cycle</Label>
              <Select value={selectedBillingCycle || "all"} onValueChange={(value) => setSelectedBillingCycle(value === "all" ? "" : value)}>
                <SelectTrigger id="billing">
                  <SelectValue placeholder="All cycles" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All cycles</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="annual">Annual</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          {(searchQuery || selectedPlanId || selectedTier || selectedBillingCycle) && (
            <div className="mt-4">
              <Button onClick={clearFilters} variant="outline" size="sm">
                Clear Filters
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Users Display */}
      {loading ? (
        <Card>
          <CardContent className="py-12 text-center">
            <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2" />
            <p className="text-muted-foreground">Loading users...</p>
          </CardContent>
        </Card>
      ) : users.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <UserIcon className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <p className="text-muted-foreground">No users found</p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Mobile Card View */}
          <div className="md:hidden space-y-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="text-lg font-semibold mb-1">Users ({totalCount} total)</h3>
                <p className="text-sm text-muted-foreground">
                  Showing {currentPage * pageSize + 1} - {Math.min((currentPage + 1) * pageSize, totalCount)}
                </p>
              </div>

              {/* Mobile Sort Controls */}
              <div className="flex gap-2 flex-shrink-0">
                <Select value={sortBy} onValueChange={(value) => {
                  setSortBy(value);
                  setCurrentPage(0);
                }}>
                  <SelectTrigger className="w-[120px] h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="date_created">Joined</SelectItem>
                    <SelectItem value="email">Email</SelectItem>
                    <SelectItem value="subscription_tier">Tier</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 w-8 p-0"
                  onClick={() => {
                    setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                    setCurrentPage(0);
                  }}
                >
                  {sortOrder === 'asc' ? (
                    <ArrowUp className="h-4 w-4" />
                  ) : (
                    <ArrowDown className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>
            {users.map((user) => (
              <Card
                key={user.id}
                className="cursor-pointer hover:bg-muted/50 transition-colors"
                onClick={() => handleViewUser(user.id)}
              >
                <CardContent className="p-4 space-y-3">
                  {/* Email and Status */}
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-sm truncate">{user.email}</h4>
                    </div>
                    <Badge variant={user.is_active ? "default" : "secondary"} className="text-xs flex-shrink-0">
                      {user.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>

                  {/* Tier and Complimentary */}
                  <div className="flex gap-2 flex-wrap">
                    <div>
                      <span className="text-xs text-muted-foreground">Tier: </span>
                      <Badge variant="outline" className="text-xs">
                        {user.subscription_tier || 'free'}
                      </Badge>
                    </div>
                    {user.is_complimentary && (
                      <Badge variant="outline" className="text-xs">
                        Complimentary
                      </Badge>
                    )}
                  </div>

                  {/* Roles */}
                  {user.roles.length > 0 && (
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Roles</p>
                      <div className="flex flex-wrap gap-1">
                        {user.roles.map((role) => (
                          <Badge key={role.id} variant="secondary" className="text-xs">
                            {role.name}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Subscription */}
                  {user.active_subscription && (
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Subscription</p>
                      <div className="text-sm">
                        <span className="font-medium">{user.active_subscription.tier}</span>
                        <span className="text-muted-foreground"> • {user.active_subscription.billing_cycle}</span>
                      </div>
                    </div>
                  )}

                  {/* Joined At */}
                  {user.date_created && (
                    <div className="pt-2 border-t">
                      <p className="text-xs text-muted-foreground mb-1">Joined</p>
                      <p className="text-xs">{new Date(user.date_created).toLocaleDateString()}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}

            {/* Mobile Pagination */}
            {Math.ceil(totalCount / pageSize) > 1 && (
              <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mt-4">
                <span className="text-sm text-muted-foreground">
                  Page {currentPage + 1} of {Math.ceil(totalCount / pageSize)}
                </span>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage((prev) => Math.max(0, prev - 1))}
                    disabled={currentPage === 0}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage((prev) => prev + 1)}
                    disabled={(currentPage + 1) * pageSize >= totalCount}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Desktop Table View */}
          <Card className="hidden md:block">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Users ({totalCount} total)</CardTitle>
                  <CardDescription>
                    Showing {currentPage * pageSize + 1} - {Math.min((currentPage + 1) * pageSize, totalCount)} of {totalCount}
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="-ml-3 h-8"
                          onClick={() => handleSort('email')}
                        >
                          Email
                          {sortBy === 'email' ? (
                            sortOrder === 'asc' ? (
                              <ArrowUp className="ml-2 h-4 w-4" />
                            ) : (
                              <ArrowDown className="ml-2 h-4 w-4" />
                            )
                          ) : (
                            <ArrowUpDown className="ml-2 h-4 w-4" />
                          )}
                        </Button>
                      </TableHead>
                      <TableHead>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="-ml-3 h-8"
                          onClick={() => handleSort('subscription_tier')}
                        >
                          Tier
                          {sortBy === 'subscription_tier' ? (
                            sortOrder === 'asc' ? (
                              <ArrowUp className="ml-2 h-4 w-4" />
                            ) : (
                              <ArrowDown className="ml-2 h-4 w-4" />
                            )
                          ) : (
                            <ArrowUpDown className="ml-2 h-4 w-4" />
                          )}
                        </Button>
                      </TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Roles</TableHead>
                      <TableHead>Subscription</TableHead>
                      <TableHead>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="-ml-3 h-8"
                          onClick={() => handleSort('date_created')}
                        >
                          Joined At
                          {sortBy === 'date_created' ? (
                            sortOrder === 'asc' ? (
                              <ArrowUp className="ml-2 h-4 w-4" />
                            ) : (
                              <ArrowDown className="ml-2 h-4 w-4" />
                            )
                          ) : (
                            <ArrowUpDown className="ml-2 h-4 w-4" />
                          )}
                        </Button>
                      </TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map((user) => (
                      <TableRow key={user.id}>
                        <TableCell className="font-medium">{user.email}</TableCell>
                        <TableCell>
                          <Badge variant="outline">
                            {user.subscription_tier || 'free'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={user.is_active ? "default" : "secondary"}
                          >
                            {user.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                          {user.is_complimentary && (
                            <Badge variant="outline" className="ml-2">
                              Complimentary
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {user.roles.length > 0 ? (
                              user.roles.map((role) => (
                                <Badge key={role.id} variant="secondary">
                                  {role.name}
                                </Badge>
                              ))
                            ) : (
                              <span className="text-muted-foreground text-sm">No roles</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {user.active_subscription ? (
                            <div className="text-sm">
                              <div className="font-medium">{user.active_subscription.tier}</div>
                              <div className="text-muted-foreground">
                                {user.active_subscription.billing_cycle}
                              </div>
                            </div>
                          ) : (
                            <span className="text-muted-foreground text-sm">No subscription</span>
                          )}
                        </TableCell>
                        <TableCell className="text-sm">
                          {user.date_created ? new Date(user.date_created).toLocaleDateString() : 'N/A'}
                        </TableCell>
                        <TableCell>
                          <Button
                            onClick={() => handleViewUser(user.id)}
                            variant="ghost"
                            size="sm"
                          >
                            <Eye className="h-4 w-4 mr-2" />
                            View
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Desktop Pagination */}
              {Math.ceil(totalCount / pageSize) > 1 && (
                <div className="flex items-center justify-between mt-4">
                  <Button
                    variant="outline"
                    onClick={() => setCurrentPage((prev) => Math.max(0, prev - 1))}
                    disabled={currentPage === 0}
                  >
                    Previous
                  </Button>
                  <span className="text-sm text-muted-foreground">
                    Page {currentPage + 1} of {Math.ceil(totalCount / pageSize)}
                  </span>
                  <Button
                    variant="outline"
                    onClick={() => setCurrentPage((prev) => prev + 1)}
                    disabled={(currentPage + 1) * pageSize >= totalCount}
                  >
                    Next
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default UserManagement;
